// TreatmentDetails.js

import React,{ useState, useEffect, useContext } from "react";
import { Grommet, Box, TextInput, Text, Main, Footer, Image, ResponsiveContext, Meter, Stack, Header, Pagination, Spinner, Select, Button } from "grommet";
import { Camera } from "grommet-icons";
import TreatmentTable from "./TreatmentTable";
import Notes from "../Base components/Notes";
import BaseCamera from "../Base components/BaseCamera";
import ExcelExport from "../Base components/ExcelExport";
import ConfirmPopupWindow from "../Base components/ConfirmPopupWindow";
import { useFetch } from "use-http";
import { getDBURL, updateStateHandler, isValidYearString, convertDateToDisplayformat, correctTimeZone } from "../Utils/Utils";
import {
    getLabelByIndex,
} from "../Utils/RBGUtils";
import _ from 'underscore'
import { UserContext } from "../../App";
import ImageUpload from "../Base components/ImageUpload";


const customTreatmentTheme = {
    global: {
        colors: {
            brand: '#7D4CDB',
            text: "#7D4CDB",
            input: "#7D4CDB",
            label: "#7D4CDB",
            border: "#7D4CDB",
            icon: "#7D4CDB",
            placeholder: "#e9d0f2",                 
        }, 

        font: {
            family: 'Roboto, sans-serif', // Choose a font you like
        },

        hover: {
            border: {
                color: "#7D4CDB",
                width: '56px',
            },
            color: "#4b2c85",
        },        
    },
    input: {
        color: "#7D4CDB",
    }, 
    date: {
        color: "#7D4CDB",
    }, 
    span: {
        color: "#7D4CDB",
    },         
    select: {
        color: "#7D4CDB",
        extend: ({ hoverIndicator }) => {
            if (hoverIndicator) {            
                return `
                    &:hover {
                        background-color: yellow;
                        transform: 'scale(1.8)';
                    }
                    `;
            }
        },
    },    
    button: {
        color: "white",
    },    
};

// Using React.memo to memoize the preview component to prevent unnecessary re-renders.
const PreviewComponent = React.memo(({ imageUrl }) => (
    <Box width="360px" height="400px" overflow="hidden" 
        style={{
            margin: '10px',
            padding: '5px' }}>

        <Image src={imageUrl} alt="Preview" opacity={imageUrl === 'BgPic.png' ? 'weak' : undefined} fit="contain" style={{ maxWidth: '100%', maxHeight: '100%' }} />
    </Box>  
));

const TreatmentDetails = (props) => {
    // const newProps = props.disable.disable ? Object.assign({}, _.omit(props, 'onClick')) : props
    // console.log('newProps >> ',newProps);
    // console.log('In TreatmentDetails props >> ',props);
    const treatmentValues = props.treatmentValues;
    const setTreatmentValues = props.setTreatmentValues;
    const [displayDate, setDisplayDate] = useState('');
    const [init, setInit] = useState(false);
    const [confirmPopupOpen, setConfirmPopupOpen] = useState(false); // Confirm Popup Window
    
    const [openUploadImageWindow, setOpenUploadImageWindow] = useState(false);    

    const currentUser = useContext(UserContext);
    const {get} = useFetch(getDBURL());

    // console.log('props.disable >> ', props.disable);
    // console.log('treatmentValues >> ',treatmentValues);
    // console.log('props.treatmentValValues >> ', props.treatmentValValues);

    // changing the location of the title on the screen resize
    const [top, setTop] = useState(
        {
            topMain:   
                (window.innerWidth < 298? '215px' :    
                window.innerWidth < 298 ? '200px' :                                
                window.innerWidth < 569 ? '185px' :   
                window.innerWidth < 751 ? '145px' : '110px')             
        }
    );

    useEffect(() => {
        function handleResize() {
            setTop({
                topMain:   
                    (window.innerWidth < 298? '215px' : 
                    window.innerWidth < 298 ? '200px' :                                
                    window.innerWidth < 569 ? '185px' :  
                    window.innerWidth < 751 ? '145px' : '110px')                                 
            })          
        }
        window.addEventListener('resize', handleResize)

        return _ => {
            window.removeEventListener('resize', handleResize)
        }
    });

    useEffect(() => {
        if (props.disable) 
            return;        

        if (treatmentValues.UpdatedAt && props.editMode) {
            setDisplayDate(convertDateToDisplayformat(treatmentValues.UpdatedAt));         
        }

        // const start = Date.now();
        const interval = setInterval(() => {
            // console.log('saving !!! ', (interval) );           
            // const millis = Math.round((Date.now() - start)/1000);
            // console.log('interval: '+ millis + ' sec');
            if(props.editMode && props.hasEdit) {
                props.saveTreatmentHandler();
            }
            
        }, 3*60000);        // 180000 = 3 minute (3 * 60000 ms)
        // }, 20000);      // 20 sec
        return () => clearInterval(interval);

    }, [treatmentValues, props.treatmentValValues]);
    
    const save = async () => {
        // console.log("in save func-n in TreatmentDetails >> ");
        // console.log('treatmentValues >> ', treatmentValues);
        return (await props.saveTreatmentHandler());
    };    

    useEffect(() => {
        if(init){
            const saveAndSetReturnToPrev = async () => {
                const result = await save();
                if (result) {
                    props.setSuccess(true);
                    props.setNavigateAfterSuccess(true);
                } else {
                    setInit(false);
                    props.setShowError(true); // optional: trigger error message
                }
            };
            
            saveAndSetReturnToPrev();  
        }      
             
    },[init]); 
 
    const [preview, setPreview] = useState('');  
    useEffect(() => {
        const getImage = async () => {
            if (props.treatmentValues.ImgExist !== 1) {
                setPreview('BgPic.png');
                return;
            }
                
            try {
                const timestamp = Date.now();
                const url = `${getDBURL()}/getImg/Treatment/${props.treatmentValues.TreatmentID}?dt=${timestamp}`;

                // Use native fetch because it can handle image blob responses
                const response = await fetch(url);
                if (!response.ok) {
                    console.error('Failed to fetch image:', response.statusText);
                    return;
                }
                
                const blob = await response.blob();
                const imageUrl = URL.createObjectURL(blob);

                setPreview(imageUrl);

            } catch (error) {
                console.error('Error checking image existence:', error);
            }
        }
        getImage();
    },[props.treatmentValues.ImgExist]); 


    const checkInputYearField = (year) => {
        const isValid = isValidYearString(year, false);
        if (!isValid.res) {
            props.setWarningMessage(isValid.msg);
            props.setShowWarning(true);
        }
        return isValid.res;
    }
    
    // opens a popup window when the "submit" button is clicked
    const openConfirmPopupWindow = () => {
        setConfirmPopupOpen(true);
    }

    // hides the "Confirm" popup window 
    const onConfirmPopupClose = () => {
        setConfirmPopupOpen(false);    
    };

    // fires when the "Confirm" button is clicked in the "Yes" popup
    const onConfirmConfirm = () => {
        submit();
        onConfirmPopupClose();
    };

    const submit = async() => {                 
        if (treatmentValues.StatusID !== 5) {
            updateStateHandler(setTreatmentValues,'StatusID', 5);        
            updateStateHandler(setTreatmentValues,'FinishedAt', correctTimeZone(new Date()));        
            updateStateHandler(setTreatmentValues,'FinishedBy', currentUser.email);
            updateStateHandler(setTreatmentValues, "LastSyncDate", correctTimeZone(new Date()));         
        }       
        setInit(true); 
    }    
        
    const complete = async () => {
        // console.log("in save func-n in TreatmentDetails >> ");
        // console.log('treatmentValues >> ', treatmentValues);
        updateStateHandler(setTreatmentValues,'StatusID', 4); 
        if(!props.newTreatment) {
            updateStateHandler(setTreatmentValues,'UpdatedAt', correctTimeZone(new Date()));        
            updateStateHandler(setTreatmentValues,'UpdatedBy', currentUser.email);
            updateStateHandler(setTreatmentValues, "LastSyncDate", correctTimeZone(new Date())); 

        }
        setInit(true); 
    }; 
    // console.log('props.disable >>',props.disable);

    const characterBoxStyle = {
        minWidth: '150px',
        maxWidth: '250px',
        padding: '2px 5px',
        marginRight: '10px',
    };

    const textStyle = {
        fontSize: '18px',
    };

    const textInputStyle = {
        border: "none",
        padding: '5px',
        fontSize: '18px',
        margin: '0',
        width: '100%',
        boxSizing: 'border-box',
    };

    const labelStyle = {
        fontWeight: "bolder",
        fontSize: "18px",
        margin: "10px 0px",
        width: "280px"
    };      

    return (
        <ResponsiveContext.Consumer>
            {screenSize => ( 
            // <Grommet theme={customTreatmentTheme}>  
            <Box  fill="vertical" flex>

                {/* {console.log('screenSize ===>>  ', screenSize)} */}
                <Box
                    align="center"
                    background="brand"
                    style={{
                        position: 'fixed',
                        width: '92%',
                        zIndex: 10,
                        border: "5px solid white"
                    }}
                >
                    {/* <Box direction="column"> */}
                    <Box direction="row-responsive" wrap>
                        <Box width="270px">
                            <Text size="large" margin="xxsmall" textAlign="center" weight="bold" color="white">
                                TREATMENT RECORD&emsp;
                            </Text>
                        </Box>
            
                        <Box direction="row" width="250px" >  
                            <TextInput
                                placeholder="Enter name"
                                style={{
                                    color: "white",
                                    fontSize: "20px",
                                    fontStyle: "italic",
                                    border: 'none',
                                    // background: "#7039db",
                                    background: "#9fbcc7",
                                    textAlign: "center",
                                    height: "36px"
                                }}
                                value={treatmentValues.TreatmentName === null ? '' : treatmentValues.TreatmentName}
                                readOnly={props.disable}
                                onChange={(event) => {
                                    updateStateHandler(setTreatmentValues, 'TreatmentName', event.target.value);
                                }}
                            />
                        </Box>
            
                        <Box width="160px" >
                            <Text size="large" margin="xxsmall" textAlign="center" weight="normal" color="white">
                                {displayDate !== "" && <strong>&emsp;({displayDate})</strong>}
                            </Text>
                        </Box>
                    </Box>
                </Box>

                <Main pad="small"
                    style={{ 
                        position: 'absolute',
                        height: '70%',
                        top: top.topMain,
                        width: '90%',
                        left: '45%',
                        transform: 'translate(-45%)',
                    }}>


                    <Box  
                        direction={screenSize === 'small' ? "column" : "row-responsive"} 
                        justify="center" width="100%" 
                        //gap="small"
                        wrap
                    >

                        {/* start of part 1 of the page */}
                        <Box 
                            width={screenSize === 'small' ? "100%" : "410px"} 
                            margin="xxsmall"  height="650px" background="transparent">
                                <Box pad="small"  border={{ size: 'xsmall' }} margin={{bottom: "5px"}}   height="375px">
                                    
                                    <Box margin={{vertical: "10px"}} >
                                        <Text alignSelf="center" color="brand" size="24px">
                                        Station Characteristics:
                                        </Text>
                                        <Box alignSelf="center" background="brand" height="3px" width="240px" margin={{vertical: "xxsmall"}}> </Box>
                                    </Box>

                                    <Box pad="xxsmall" direction="row">
                                        <Box  style={characterBoxStyle}>
                                            <Text style={textStyle}>
                                            Rootstock:
                                            </Text>
                                        </Box>
                                        <TextInput type="text"
                                            value={props.selectedStation.Rootstock === null ? '' : props.selectedStation.Rootstock}
                                            size="medium"   
                                            style={textInputStyle}                        
                                        />                            
                                    </Box>              

                                    <Box pad="xxsmall" direction="row">
                                        <Box  style={characterBoxStyle}>
                                            <Text style={textStyle}>
                                            Planting year:
                                            </Text>
                                        </Box>
                                        <TextInput type="text"
                                            value={props.selectedStation.Year === null ? '' : props.selectedStation.Year}
                                            size="medium"   
                                            style={textInputStyle}                        
                                        />                            
                                    </Box> 

                                    <Box pad="xxsmall" direction="row">
                                        <Box  style={characterBoxStyle} >
                                            <Text style={textStyle}>
                                            Bud-Break Date:
                                            </Text>
                                        </Box>
                                        <TextInput type="text"
                                            value={props.phenologicalPhasesDates.BudBreakDate === null ? '' : convertDateToDisplayformat(props.phenologicalPhasesDates.BudBreakDate)}
                                            size="medium"   
                                            style={textInputStyle}                        
                                        />                            
                                    </Box>                     

                                    <Box pad="xxsmall" direction="row">
                                        <Box style={characterBoxStyle}>
                                            <Text style={textStyle}>
                                            Flowering Date:
                                            </Text>
                                        </Box>
                                        <TextInput type="text"
                                            value={props.phenologicalPhasesDates.FloweringDate === null ? '' : convertDateToDisplayformat(props.phenologicalPhasesDates.FloweringDate)}
                                            size="medium"   
                                            style={textInputStyle}                        
                                        />                            
                                    </Box>                     

                                    <Box pad="xxsmall" direction="row">
                                        <Box  style={characterBoxStyle}>
                                            <Text style={textStyle}>
                                            Veraison Date:
                                            </Text>
                                        </Box>
                                        <TextInput type="text"
                                            value={props.phenologicalPhasesDates.VeraisonDate === null ? '' : convertDateToDisplayformat(props.phenologicalPhasesDates.VeraisonDate)}
                                            size="medium"   
                                            style={textInputStyle}                         
                                        />                            
                                    </Box>    

                                    <Box pad="xxsmall" direction="row">
                                        <Box  style={characterBoxStyle}>
                                            <Text style={textStyle}>
                                            Ripeness Date:
                                            </Text>
                                        </Box>
                                        <TextInput type="text"
                                            value={props.phenologicalPhasesDates.RipenessDate === null ? '' : convertDateToDisplayformat(props.phenologicalPhasesDates.RipenessDate)}
                                            size="medium"   
                                            style={textInputStyle}                       
                                        />                            
                                    </Box>       

                                </Box>

                                <Box pad="small"  border={{ size: 'xsmall' }} margin={{bottom: "5px"}}   height="275px">
                                    <Box margin={{vertical: "10px"}} >
                                        <Text alignSelf="center" color="brand" size="24px">
                                        Vine Characteristics:
                                        </Text>
                                        <Box alignSelf="center" background="brand" height="3px" width="220px" margin={{vertical: "xxsmall"}}> </Box>
                                    </Box>

                                    <Box pad="xxsmall" direction="row">
                                        <Box  style={characterBoxStyle}>
                                            <Text alignSelf="start" color="brand" size="18px">
                                            Type:
                                            </Text>
                                        </Box>
                                        <TextInput type="text"
                                            value={props.cultivarData.Type === null ? '' : getLabelByIndex("Type", props.cultivarData.Type)}
                                            size="medium"   
                                            style={textInputStyle}                         
                                        />                            
                                    </Box>    

                                    <Box pad="xxsmall" direction="row">
                                        <Box  style={characterBoxStyle}>
                                            <Text alignSelf="start" color="brand" size="18px">
                                            Bud Break:
                                            </Text>
                                        </Box>
                                        <TextInput type="text"
                                            value={props.cultivarData.BudBreak === null ? '' : getLabelByIndex("BudBreak", props.cultivarData.BudBreak)}
                                            size="medium"   
                                            style={textInputStyle}                         
                                        />                            
                                    </Box>     

                                    <Box pad="xxsmall" direction="row">
                                        <Box  style={characterBoxStyle}>
                                            <Text alignSelf="start" color="brand" size="18px">
                                            Rain Resistance:
                                            </Text>
                                        </Box>
                                        <TextInput type="text"
                                            value={props.cultivarData.RainResistance === null ? '' : getLabelByIndex("RainResistance", props.cultivarData.RainResistance)}
                                            size="medium"   
                                            style={textInputStyle}                         
                                        />                            
                                    </Box> 

                                    <Box pad="xxsmall" direction="row">
                                        <Box  style={characterBoxStyle}>
                                            <Text alignSelf="start" color="brand" size="18px">
                                            Chilling:
                                            </Text>
                                        </Box>
                                        <TextInput type="text"
                                            value={props.cultivarData.Chilling === null ? '' : getLabelByIndex("Chilling", props.cultivarData.Chilling)}
                                            size="medium"   
                                            style={textInputStyle}                         
                                        />                            
                                    </Box>  
                                </Box> 
                        </Box>
                        {/* end of part 1 of the page */}


                        {/* start of part 2 of the page */}
                        <Box 
                            width={screenSize === 'small' ? "100%" : "410px"} 
                            // width={screenSize === 'large' ? "413px" : "medium" ? "50%" : "100%" } 
                            margin="xxsmall"  height="650px" background="transparent">
                            <Box pad="small"  border={{ size: 'xsmall' }} margin={{bottom: "5px"}}   height="375px">

                                {openUploadImageWindow && 
                                <Box>
                                    <ImageUpload 
                                        onClose={setOpenUploadImageWindow} 
                                        docType="Treatment"
                                        docId={props.treatmentValues.TreatmentID}
                                        stateHandler={setTreatmentValues}
                                        initValue={treatmentValues.ImgExist}
                                        fieldName="ImgExist"
                                        tableName="VEM_Treatments"
                                        updateImage={setPreview}
                                        disable={props.disable}                            
                                    />
                                </Box>} 

                                <Box align="center" height="400px">
                                    <PreviewComponent imageUrl={preview} />
                                </Box>
                                <Box align="end" width="360px">
                                    <Box width= "60px" >
                                        <Button   
                                            style={{color: "#ffffff", textAlign: "center", padding: "4px"}}   
                                            icon={<Camera color='white'/>}                            
                                            primary                            
                                            //label="add image"
                                            onClick={() => setOpenUploadImageWindow(true)} 
                                            disabled={props.disable} 
                                        />
                                    </Box> 
                                </Box>                                         
                                
                            </Box>

                            <Box pad="small"  border={{ size: 'xsmall' }} margin={{bottom: "5px"}}   height="275px">
                                <Box width="540px" height="250px">
                                    <Notes
                                        stateHandler={setTreatmentValues}
                                        initValue={treatmentValues.Notes}
                                        disable={props.disable}
                                        fieldName="Notes"
                                    ></Notes>
                                </Box>    

                            </Box> 
                        </Box>       
                        {/* end of part 2 of the page */}


                        {/* start of part 3 of the page */}
                        {/* {console.log('window.innerWidth ===>>  ', window.innerWidth)} */}
                        <Box
                            margin="xxsmall"
                            background="transparent"
                            height={(screenSize > "large") ? "650px" : "100%"} 
                            wrap                    
                        >
                            <Box
                                flex={true}
                                align='center'
                                border={{ size:  screenSize > "large" ? 'xsmall' : undefined }}
                                // border={{ size:  window.innerWidth > "1800px" ? 'xsmall' : "none" }}
                                margin={{ bottom: "5px" }}
                            >
                                <Box
                                    flex={false}
                                    style={{
                                    maxHeight:  screenSize > "large" ? "90%" : "110%"
                                    }}                               
                                >
                                    <TreatmentTable
                                        tableWdth={screenSize === 'small' ? "100%" : "825px"} 
                                        tableHeight= "100%"
                                        treatmentValues={props.treatmentValValues}
                                        setTreatmentValues={props.setTreatmentValValues}
                                        stateHandler={setTreatmentValues}
                                        disable={props.disable}
                                        isDefault={false}
                                        treatmentProperties={treatmentValues}
                                        newTreatment={props.newTreatment}
                                        templatesComboList={props.templatesComboList}
                                        selectedTemplate={props.selectedTemplate}
                                        onChangeSelected={props.onChangeSelected}
                                    />
                                </Box>
                            </Box>
                        </Box>        
                        {/* end of part 3 of the page */}


                    </Box> 

                    {confirmPopupOpen && (
                        <ConfirmPopupWindow
                            msg={
                                "This treatment will be finished! Are you sure you want to continue?"
                            }
                            onClose={onConfirmPopupClose}
                            onConfirm={onConfirmConfirm}
                        />
                    )}                  
                </Main>

                <Footer pad="medium" direction="row-responsive" alignSelf="center" justify="center"
                    style={{ 
                        position: 'fixed',
                        width: (screenSize === 'xsmall' || screenSize === 'small') ? '200px' : '90%',
                        bottom: '7%',
                    }}
                    >   
                    <Box >
                        <Button 
                            style={{width: "170px", margin: "0 auto", background: "#AA3747", border: "1px solid #AA3747"}} 
                            label="Finish" 
                            disabled={props.disable} 
                            onClick={openConfirmPopupWindow}  primary />   
                    </Box>        
                    <Box >         
                        <Button 
                            style={{width: "170px", margin: "0 auto", background: "#039039", border: "1px solid #039039"}} 
                            label="Save & Exit" 
                            disabled={props.disable} 
                            onClick={complete}  primary />
                    </Box>
                    <Box  justify="center" >
                        <Button style={{width: "170px", margin: "0 auto"}} 
                            label="Save Draft"
                            disabled={props.disable} 
                            onClick={save} primary />
                    </Box>
                </Footer>      
            </Box>
            // </Grommet>
            )}
        </ResponsiveContext.Consumer>

  );
};

export default TreatmentDetails;




